import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../api/api";
import "./Login.css";

function Auth() {
  const navigate = useNavigate();
  const location = useLocation();

  // 1. Detect which page we are on based on the URL route
  const isSignupRoute = location.pathname.includes("signup");

  // 2. Intelligently set the default state based on the route
  const [mode, setMode] = useState(isSignupRoute ? "signup" : "login");
  const [step, setStep] = useState(isSignupRoute ? "details" : "email");
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  const [form, setForm] = useState({
    email: "",
    name: "",
    password: "",
    otp: "",
  });

  // 3. Listen for URL changes in case they click the header buttons while already on this page
  useEffect(() => {
    resetMessages();
    if (location.pathname.includes("signup")) {
      setMode("signup");
      setStep("details");
    } else {
      setMode("login");
      setStep("email");
    }
  }, [location.pathname]);

  const isValidEmail = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email), [form.email]);
  const isValidOtp = useMemo(() => /^\d{4,6}$/.test(form.otp), [form.otp]);

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const resetMessages = () => {
    setMessage("");
    setError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    resetMessages();

    if (name === "otp") {
      setForm((prev) => ({ ...prev, otp: value.replace(/\D/g, "").slice(0, 6) }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveAuthData = (data) => {
    if (data?.token) localStorage.setItem("token", data.token);
    if (data?.user) localStorage.setItem("user", JSON.stringify(data.user));
  };

  const checkEmail = async () => {
    resetMessages();
    if (!isValidEmail) return setError("Please enter a valid email address.");

    try {
      setLoading(true);
      const res = await api.post("api/auth/check-email", { email: form.email });
      
      if (res.data?.exists) {
        setMode("login");
        setStep("password");
      } else {
        // If email doesn't exist, redirect them to the formal signup route
        navigate("/signup");
        setMessage("New email detected. Please set up your account.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Unable to check email.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    resetMessages();
    if (!form.password) return setError("Please enter your password.");

    try {
      setLoading(true);
      const res = await api.post("api/auth/login", {
        email: form.email,
        password: form.password,
      });

      saveAuthData(res.data);
      setMessage(res.data?.message || "Login successful.");
      setTimeout(() => navigate("/"), 500);
    } catch (err) {
      setError(err.response?.data?.message || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignupSendOtp = async () => {
    resetMessages();
    if (!form.name.trim()) return setError("Please enter your full name.");
    if (!isValidEmail) return setError("Please enter a valid email address.");
    if (!form.password || form.password.length < 6) return setError("Password must be at least 6 characters.");

    try {
      setLoading(true);
      const res = await api.post("api/auth/signup/send-otp", {
        name: form.name.trim(),
        email: form.email,
        password: form.password,
      });

      setStep("otp");
      setMessage(res.data?.message || "OTP sent to your email.");
      setResendTimer(30);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignupVerify = async () => {
    resetMessages();
    if (!isValidOtp) return setError("Please enter a valid OTP.");

    try {
      setLoading(true);
      const res = await api.post("api/auth/signup/verify", {
        name: form.name.trim(),
        email: form.email,
        password: form.password,
        otp: form.otp,
      });

      saveAuthData(res.data);
      setMessage(res.data?.message || "Account created successfully.");
      setTimeout(() => navigate("/"), 500);
    } catch (err) {
      setError(err.response?.data?.message || "Invalid OTP.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>{mode === "signup" ? "Create Account" : "Login"}</h2>
        <p className="auth-subtext">
          {step === "otp" ? "Verify your email address." : "Continue with your email address."}
        </p>

        {step !== "otp" && (
          <div className="auth-field">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="Enter your email address"
              value={form.email}
              onChange={handleChange}
              disabled={loading || (step !== "email" && step !== "details")}
            />
          </div>
        )}

        {step === "details" && (
          <div className="auth-field">
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              name="name"
              type="text"
              placeholder="Enter your full name"
              value={form.name}
              onChange={handleChange}
              disabled={loading}
            />
          </div>
        )}

        {(step === "password" || step === "details") && (
          <div className="auth-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              placeholder={mode === "signup" ? "Create a password" : "Enter your password"}
              value={form.password}
              onChange={handleChange}
              disabled={loading}
            />
          </div>
        )}

        {step === "otp" && (
          <div className="auth-field">
            <label htmlFor="otp">Email Verification Code</label>
            <input
              id="otp"
              name="otp"
              type="text"
              inputMode="numeric"
              placeholder="Enter 6-digit OTP"
              value={form.otp}
              onChange={handleChange}
              disabled={loading}
            />
          </div>
        )}

        {message && <p className="auth-message success">{message}</p>}
        {error && <p className="auth-message error">{error}</p>}

        {/* Buttons based on current step */}
        {step === "email" && (
          <button className="auth-btn" onClick={checkEmail} disabled={loading}>
            {loading ? "Checking..." : "Continue"}
          </button>
        )}

        {step === "password" && (
          <>
            <button className="auth-btn" onClick={handleLogin} disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
              <button
                className="auth-link-btn"
                type="button"
                onClick={() => { setStep("email"); resetMessages(); form.password = ""; }}
                disabled={loading}
              >
                Change Email
              </button>
              <button
                className="auth-link-btn"
                type="button"
                onClick={() => navigate("/forgot-password")}
                disabled={loading}
                style={{ color: '#d9534f' }} 
              >
                Forgot Password?
              </button>
            </div>
          </>
        )}

        {step === "details" && (
          <>
            <button className="auth-btn" onClick={handleSignupSendOtp} disabled={loading}>
              {loading ? "Sending Code..." : "Continue"}
            </button>
          </>
        )}

        {step === "otp" && (
          <>
            <button className="auth-btn" onClick={handleSignupVerify} disabled={loading}>
              {loading ? "Verifying..." : "Verify & Create Account"}
            </button>
            <button
              className="auth-btn secondary"
              onClick={handleSignupSendOtp}
              disabled={loading || resendTimer > 0}
              type="button"
            >
              {resendTimer > 0 ? `Resend Code in ${resendTimer}s` : "Resend Code"}
            </button>
            <button
              className="auth-link-btn"
              type="button"
              onClick={() => { setStep("details"); setForm((prev) => ({ ...prev, otp: "" })); resetMessages(); }}
              disabled={loading}
            >
              Back to Details
            </button>
          </>
        )}

        {/* Manual Signup/Login Toggle Footer */}
        {step !== "otp" && (
          <div className="auth-footer" style={{ marginTop: '20px', textAlign: 'center', fontSize: '0.9rem' }}>
            {mode === "login" ? (
              <p>
                Don't have an account?{" "}
                <span 
                  style={{ color: '#007bff', cursor: 'pointer', fontWeight: 'bold' }}
                  onClick={() => navigate("/signup")}
                >
                  Sign up
                </span>
              </p>
            ) : (
              <p>
                Already have an account?{" "}
                <span 
                  style={{ color: '#007bff', cursor: 'pointer', fontWeight: 'bold' }}
                  onClick={() => navigate("/login")}
                >
                  Log in
                </span>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Auth;